/**
 * /admin/pre-deploy-checklist — interactive view of
 * /app/memory/PRE_DEPLOY_CHECKLIST.md.
 *
 * Same UX pattern as AdminQAChecklist: tickboxes with localStorage
 * persistence, progress bar, per-section completion counts. Differs only
 * in that sections carry a severity pill (critical / important /
 * nice / done) so launch-blockers stand out visually from polish work.
 *
 * Admin/staff/owner gated. Owners use this in the run-up to flipping
 * production live — it's a single-page interactive checkpoint instead
 * of flipping between the markdown file and Stripe / Resend dashboards.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Rocket, RotateCcw, AlertTriangle, Info, Sparkles, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { isStaffOrAbove } from '../lib/tier';
import { PRE_DEPLOY_CHECKLIST } from '../data/pre-deploy-checklist';
import PageTitle from '../components/PageTitle';

const STORAGE_KEY = 'forge.pre-deploy-checklist.v1';

const SEVERITY_STYLES = {
  critical:  { bg: 'bg-[#FEF2F2]', text: 'text-[#B91C1C]', label: 'Critical', Icon: AlertTriangle },
  important: { bg: 'bg-[#FFFBEB]', text: 'text-[#B45309]', label: 'Important', Icon: Info },
  nice:      { bg: 'bg-[#ECFDF5]', text: 'text-[#047857]', label: 'Nice-to-have', Icon: Sparkles },
  done:      { bg: 'bg-[#F1F5F9]', text: 'text-[#475569]', label: 'Done', Icon: CheckCircle2 },
};

const loadTicks = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const saveTicks = (ticks) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(ticks)); } catch { /* private mode no-op */ }
};

// Build the initial tick state honouring `defaultChecked` (e.g. the
// "Already done" section ships pre-ticked). When `skipStored` is true
// the localStorage overlay is ignored — used by Reset so the user's
// ticks don't get rebuilt from the still-cached storage layer before
// it's been cleared.
const buildInitial = ({ skipStored = false } = {}) => {
  const stored = skipStored ? null : loadTicks();
  const base = {};
  PRE_DEPLOY_CHECKLIST.forEach((s) => s.groups.forEach((g) => g.items.forEach((i) => {
    if (i.defaultChecked) base[i.id] = true;
  })));
  return { ...base, ...(stored || {}) };
};

const AdminPreDeployChecklist = () => {
  const { user } = useAuth();
  const [ticks, setTicks] = useState({});

  useEffect(() => { setTicks(buildInitial()); }, []);

  const allItems = useMemo(
    () => PRE_DEPLOY_CHECKLIST.flatMap((s) => s.groups.flatMap((g) => g.items)),
    [],
  );
  const blockerItems = useMemo(
    () => PRE_DEPLOY_CHECKLIST.find((s) => s.severity === 'critical')
      ?.groups.flatMap((g) => g.items) || [],
    [],
  );
  const completedCount = allItems.filter((i) => ticks[i.id]).length;
  const totalCount = allItems.length;
  const pct = totalCount === 0 ? 0 : Math.round((completedCount / totalCount) * 100);
  const blockerRemaining = blockerItems.filter((i) => !ticks[i.id]).length;
  const launchReady = blockerRemaining === 0;

  const toggle = (id) => {
    setTicks((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      saveTicks(next);
      return next;
    });
  };

  const resetAll = () => {
    if (!window.confirm('Clear every tick? "Already done" rows will return to ticked. This cannot be undone.')) return;
    // Order matters: clear localStorage first so buildInitial() doesn't
    // re-read the about-to-be-discarded ticks.
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* no-op */ }
    setTicks(buildInitial({ skipStored: true }));
  };

  if (!user) return <Navigate to="/login" replace />;
  if (!isStaffOrAbove(user)) return <Navigate to="/home" replace />;

  return (
    <div className="p-6 sm:p-8 max-w-4xl mx-auto" data-testid="pre-deploy-checklist-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <PageTitle
          title="Pre-deploy checklist"
          subtitle="Source of truth: /app/memory/PRE_DEPLOY_CHECKLIST.md. Ticks save to your browser."
          className="mb-0"
        />
        <Button onClick={resetAll} className="bg-white border border-[#E2E8F0] text-[#475569] hover:bg-[#F8FAFC]" data-testid="pre-deploy-checklist-reset">
          <RotateCcw className="w-4 h-4 mr-2" />Reset
        </Button>
      </div>

      {/* Launch-ready banner */}
      <div
        className={`mb-6 rounded-xl border p-4 flex items-start gap-3 ${
          launchReady ? 'bg-[#ECFDF5] border-[#A7F3D0]' : 'bg-[#FEF2F2] border-[#FECACA]'
        }`}
        data-testid="pre-deploy-launch-banner"
      >
        {launchReady ? <CheckCircle2 className="w-5 h-5 text-[#047857] mt-0.5" /> : <AlertTriangle className="w-5 h-5 text-[#B91C1C] mt-0.5" />}
        <div className="flex-1">
          <div className={`text-sm font-bold ${launchReady ? 'text-[#065F46]' : 'text-[#7F1D1D]'}`}>
            {launchReady ? 'All critical items ticked — clear to ship.' : `${blockerRemaining} launch-blocker${blockerRemaining === 1 ? '' : 's'} remaining.`}
          </div>
          <div className={`text-xs mt-0.5 ${launchReady ? 'text-[#065F46]' : 'text-[#7F1D1D]'}`}>
            {launchReady
              ? 'Important + nice-to-have items can be picked up post-launch without blocking the flip-live.'
              : 'Anything in the red section must be ticked off before flipping production live.'}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-8" data-testid="pre-deploy-checklist-progress">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-xs uppercase tracking-wider font-bold text-[#64748B]">Progress</span>
          <span className="text-sm font-extrabold text-[#020617]">{completedCount} / {totalCount} · {pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-[#E2E8F0] overflow-hidden">
          <div
            className="h-full bg-[#0052FF] transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <div className="space-y-10">
        {PRE_DEPLOY_CHECKLIST.map((section) => (
          <Section key={section.section} section={section} ticks={ticks} onToggle={toggle} />
        ))}
      </div>
    </div>
  );
};

const Section = ({ section, ticks, onToggle }) => {
  const sectionItems = section.groups.flatMap((g) => g.items);
  const sectionDone = sectionItems.filter((i) => ticks[i.id]).length;
  const sev = SEVERITY_STYLES[section.severity] || SEVERITY_STYLES.important;
  const { Icon } = sev;
  return (
    <section data-testid={`pre-deploy-section-${section.severity}`}>
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <h2 className="text-xl font-extrabold text-[#020617] tracking-tight">{section.section}</h2>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${sev.bg} ${sev.text}`}>
          <Icon className="w-3 h-3" />
          {sev.label}
        </span>
      </div>
      <p className="text-sm text-[#64748B] mb-1">{section.blurb}</p>
      <p className="text-xs font-medium text-[#0052FF] mb-4">{sectionDone} / {sectionItems.length} done</p>
      <div className="space-y-5">
        {section.groups.map((g) => (
          <Group key={g.title} group={g} severity={section.severity} ticks={ticks} onToggle={onToggle} />
        ))}
      </div>
    </section>
  );
};

const Group = ({ group, severity, ticks, onToggle }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
    <h3 className="text-sm font-bold text-[#020617] mb-1">{group.title}</h3>
    {group.note && (
      <p className="text-xs text-[#64748B] mb-3 italic">{group.note}</p>
    )}
    <ul className="space-y-2 mt-2">
      {group.items.map((item) => {
        const checked = !!ticks[item.id];
        // "Already done" items get strikethrough by default for the audit
        // trail — same visual treatment as the markdown source.
        const isHistoric = severity === 'done';
        return (
          <li key={item.id} className="flex items-start gap-3" data-testid={`pre-deploy-item-${item.id}`}>
            <Checkbox
              checked={checked}
              onCheckedChange={() => onToggle(item.id)}
              className="mt-0.5"
              aria-label={item.label}
            />
            <span className={`text-sm ${(checked || isHistoric) ? 'text-[#94A3B8] line-through' : 'text-[#334155]'}`}>
              {item.label}
            </span>
          </li>
        );
      })}
    </ul>
  </div>
);

export default AdminPreDeployChecklist;
